import { FieldShell } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * Props for {@link TenantGeneralSettingsView} — the console's GENERAL section on
 * the platform plane (TO-125): the tenant's default offer expiry.
 *
 * @property days - The field's text (`''` = no default expiry).
 * @property fieldError - A client-side refusal shown under the field, or null.
 * @property saveError - The server's refusal of a save, announced, or null.
 * @property saving - True while a save is in flight; disables Save.
 */
export interface TenantGeneralSettingsViewProps {
	days: string
	fieldError: string | null
	saveError: string | null
	saving: boolean
	onChange: (days: string) => void
	onSave: () => void
}

const INPUT =
	'h-10 w-32 rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

/** The Super-Admin's default offer expiry editor (organism, TO-125). */
export function TenantGeneralSettingsView(props: TenantGeneralSettingsViewProps): ReactElement {
	return (
		<form
			// The section validates and words the refusal itself (TO-125 BR-10).
			noValidate
			className="flex flex-col gap-3"
			onSubmit={(event) => {
				event.preventDefault()
				props.onSave()
			}}
		>
			<FieldShell
				label="Default offer expiry (days)"
				description="New offers expire this many days after they are created. Leave it empty for no expiry."
				error={props.fieldError ?? undefined}
			>
				<input
					// Text, not number: a number input turns "abc" into "" (= no expiry); the raw text is validated instead.
					type="text"
					inputMode="numeric"
					className={INPUT}
					value={props.days}
					onChange={(event) => props.onChange(event.target.value)}
				/>
			</FieldShell>
			{props.saveError ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.saveError}
				</p>
			) : null}
			<div>
				<Button type="submit" isDisabled={props.saving}>
					Save
				</Button>
			</div>
		</form>
	)
}
